import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../services/tokenService';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { ShieldCheck, ScrollText, CheckCircle2, XCircle, Loader2, Plus, ArrowLeft, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = () => ({ headers: getAuthHeader() });

export default function AdminSponsorImpactPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [conv, setConv] = useState({ sponsor_id: '', title: '', amount_total: '', start_date: '', end_date: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, r] = await Promise.all([
        axios.get(`${API}/api/admin/sponsors/impact/a-valider`, cfg()),
        axios.get(`${API}/api/admin/sponsors`, cfg()),
        axios.get(`${API}/api/admin/sponsors/renouvellements/a-traiter`, cfg()),
      ]);
      setPending(p.data.a_valider || []);
      setSponsors(s.data.sponsors || []);
      setRenewals(r.data.renouvellements || []);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erreur de chargement.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const processRenewal = async (id, decision) => {
    try {
      let newEnd = null;
      if (decision === 'APPROVED') newEnd = window.prompt('Nouvelle date de fin (AAAA-MM-JJ) :') || null;
      await axios.post(`${API}/api/admin/sponsors/renouvellements/${id}/traiter`, { decision, new_end_date: newEnd }, cfg());
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  const review = async (id, decision, isPublic) => {
    try {
      await axios.post(`${API}/api/admin/sponsors/impact/mesures/${id}/valider`,
        { decision, is_public: isPublic, comment: '' }, cfg());
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  const saveConvention = async () => {
    setMsg('');
    if (!conv.sponsor_id || !conv.title) { setMsg('Sponsor et titre requis.'); return; }
    try {
      await axios.post(`${API}/api/admin/sponsors/convention`, {
        sponsor_id: conv.sponsor_id, title: conv.title,
        amount_total: parseFloat(conv.amount_total) || null,
        start_date: conv.start_date || null, end_date: conv.end_date || null,
      }, cfg());
      setMsg('Convention enregistrée.');
      setConv({ sponsor_id: '', title: '', amount_total: '', start_date: '', end_date: '' });
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  return (
    <div className="min-h-screen bg-slate-950 py-10 px-6" data-testid="admin-sponsor-impact-page">
      <div className="max-w-5xl mx-auto space-y-10">
        <div>
          <button onClick={() => navigate('/admin/super-dashboard')} data-testid="admin-impact-back" className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-3">
            <ArrowLeft className="w-4 h-4" /> Retour console Super Admin
          </button>
          <p className="text-emerald-400 text-sm font-medium uppercase tracking-wider">O'SCOP — Sponsors</p>
          <h1 className="text-white text-3xl font-bold mt-1">Validation d'impact & Conventions</h1>
        </div>
        {msg && <p className="text-amber-300 text-sm" data-testid="admin-impact-msg">{msg}</p>}

        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 text-emerald-400 mb-4"><ShieldCheck className="w-5 h-5" /><h2 className="text-white font-semibold">Mesures à valider</h2></div>
          {loading && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>}
          {!loading && pending.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-impact-empty">Aucune mesure en attente.</p>}
          <ul className="space-y-2">
            {pending.map((m) => (
              <li key={m.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 text-sm" data-testid={`admin-meas-${m.id}`}>
                <span className="text-white">{m.indicator_name} : {m.value} {m.unit} <span className="text-slate-500 text-xs">· {m.period}</span></span>
                <div className="flex items-center gap-2">
                  <button onClick={() => review(m.id, 'VALIDATED', true)} data-testid={`admin-validate-public-${m.id}`} className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Valider + public</button>
                  <button onClick={() => review(m.id, 'VALIDATED', false)} data-testid={`admin-validate-${m.id}`} className="text-emerald-500/70 hover:text-emerald-400 text-xs">Valider</button>
                  <button onClick={() => review(m.id, 'REJECTED', false)} data-testid={`admin-reject-${m.id}`} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"><XCircle className="w-3 h-3" />Rejeter</button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="admin-renewals-section">
          <div className="flex items-center gap-2 text-emerald-400 mb-4"><RefreshCw className="w-5 h-5" /><h2 className="text-white font-semibold">Demandes de renouvellement</h2></div>
          {renewals.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-renewals-empty">Aucune demande en attente.</p>}
          <ul className="space-y-2">
            {renewals.map((r) => (
              <li key={r.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 text-sm" data-testid={`admin-renewal-${r.id}`}>
                <div>
                  <span className="text-white">{r.organisation}</span>
                  <span className="text-slate-500 ml-2 text-xs">{r.proposed_start_date || '—'} → {r.proposed_end_date || '—'}</span>
                  {r.message && <p className="text-slate-500 text-xs mt-0.5">« {r.message} »</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => processRenewal(r.id, 'APPROVED')} data-testid={`admin-renewal-approve-${r.id}`} className="text-emerald-400 hover:text-emerald-300 text-xs">Approuver</button>
                  <button onClick={() => processRenewal(r.id, 'REJECTED')} data-testid={`admin-renewal-reject-${r.id}`} className="text-red-400 hover:text-red-300 text-xs">Rejeter</button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 text-emerald-400 mb-4"><ScrollText className="w-5 h-5" /><h2 className="text-white font-semibold">Créer / mettre à jour une convention</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={conv.sponsor_id} onChange={(e) => setConv({ ...conv, sponsor_id: e.target.value })} data-testid="conv-sponsor-select" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Sponsor…</option>
              {sponsors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input value={conv.title} onChange={(e) => setConv({ ...conv, title: e.target.value })} placeholder="Titre de la convention" data-testid="conv-title-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <input type="number" value={conv.amount_total} onChange={(e) => setConv({ ...conv, amount_total: e.target.value })} placeholder="Montant total €" data-testid="conv-amount-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <DatePickerFr value={conv.start_date} onChange={(v) => setConv({ ...conv, start_date: v })} id="conv-start-input" className="w-full" />
              <DatePickerFr value={conv.end_date} onChange={(v) => setConv({ ...conv, end_date: v })} id="conv-end-input" className="w-full" />
            </div>
          </div>
          <button onClick={saveConvention} disabled={!conv.sponsor_id || !conv.title} data-testid="conv-save-btn" className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1"><Plus className="w-4 h-4" />Enregistrer</button>
        </section>
      </div>
    </div>
  );
}
